/**
 * AccountHospitalitySection — Hospitality & Gifts card + dialogs for AccountOverview.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Collapsible, CollapsibleContent } from '../../ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Skeleton } from '../../ui/skeleton';
import { Gift, FileText, Download, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { useConfirm } from '../../common/ConfirmDialog';
import { SectionHeader } from '../../common/OverviewHelpers';
import { HospitalityDialog, HospitalityDetailDialog } from '../ActivityDialogs';

const SectionSkeleton = ({ rows = 2 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
  </div>
);

const CURRENCY_SYMBOLS = { USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$' };
const CATEGORY_LABELS = {
  gift: 'Gift',
  hospitality_dining: 'Dining',
  hospitality_experience: 'Experience',
  hospitality_travel: 'Travel',
  hospitality_meeting: 'Meeting',
};

const AccountHospitalitySection = ({
  account, canEditAccount,
  hospitality = [], hospLoading,
  invalidateHospitality,
  allAssociations = [], usersMap = {},
}) => {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [hospDialogOpen, setHospDialogOpen] = useState(false);
  const [hospDetailOpen, setHospDetailOpen] = useState(false);
  const [selectedHospitality, setSelectedHospitality] = useState(null);

  // File preview state
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [previewFileType, setPreviewFileType] = useState(null);
  const [previewFileId, setPreviewFileId] = useState(null);
  const [isEditingFile, setIsEditingFile] = useState(false);
  const [editFileDescription, setEditFileDescription] = useState('');
  const [updatingFile, setUpdatingFile] = useState(false);

  const openFilePreview = async (fileId) => {
    if (!fileId) { toast.error('No file available'); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${window.location.origin}/api/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        setPreviewFileUrl(URL.createObjectURL(blob));
        setPreviewFileType(blob.type);
        setPreviewFileId(fileId);
        setEditFileDescription('Receipt');
        setFilePreviewOpen(true);
      } else {
        toast.error('File not found or no longer available');
      }
    } catch { toast.error('Failed to open file'); }
  };

  const handleDeletePreviewFile = async () => {
    if (!previewFileId) return;
    if (!await confirm({ title: 'Delete this file?', destructive: true })) return;
    setUpdatingFile(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${window.location.origin}/api/files/${previewFileId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) { toast.success('File deleted'); setFilePreviewOpen(false); }
      else throw new Error('Failed to delete');
    } catch { toast.error('Failed to delete file'); }
    finally { setUpdatingFile(false); }
  };

  const handleUpdateFileDescription = async () => {
    if (!previewFileId || !editFileDescription.trim()) return;
    setUpdatingFile(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${window.location.origin}/api/files/${previewFileId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ description: editFileDescription }),
      });
      if (response.ok) { toast.success('File updated'); setIsEditingFile(false); }
      else throw new Error('Failed to update');
    } catch { toast.error('Failed to update file'); }
    finally { setUpdatingFile(false); }
  };

  const fmtCurrency = (item) => {
    const symbol = CURRENCY_SYMBOLS[item.currency] || item.currency || '$';
    return `${symbol}${item.monetary_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <Collapsible open={open} onOpenChange={setOpen}>
            <SectionHeader
              title="Hospitality & Gifts"
              icon={Gift}
              isOpen={open}
              onToggle={() => setOpen(o => !o)}
              action={canEditAccount ? () => setHospDialogOpen(true) : undefined}
            />
            <CollapsibleContent className="mt-4">
              {hospLoading ? <SectionSkeleton rows={2} /> : hospitality.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No hospitality records.</p>
              ) : (
                <div className="space-y-2">
                  {hospitality.map(item => (
                    <div
                      key={item.id}
                      className="p-3 border rounded text-sm hover:bg-muted/50 cursor-pointer"
                      onClick={() => { setSelectedHospitality(item); setHospDetailOpen(true); }}
                      data-testid={`hosp-${item.id}`}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <Badge variant="secondary" className="text-xs flex items-center gap-1">
                          <Gift className="w-3 h-3" />
                          {CATEGORY_LABELS[item.category] || item.category}
                        </Badge>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground text-xs">{new Date(item.date).toLocaleDateString()}</span>
                          {item.receipt_file_id && (
                            <FileText
                              className="w-4 h-4 text-primary cursor-pointer hover:text-primary/80"
                              onClick={e => { e.stopPropagation(); openFilePreview(item.receipt_file_id); }}
                              title="View receipt"
                            />
                          )}
                        </div>
                      </div>
                      <p className="font-medium">{item.short_summary}</p>
                      {item.monetary_value > 0 && <p className="text-xs text-muted-foreground mt-1">{fmtCurrency(item)}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      <HospitalityDialog
        open={hospDialogOpen}
        onOpenChange={setHospDialogOpen}
        accountId={account?.id}
        associations={allAssociations}
        onSuccess={invalidateHospitality}
      />

      <HospitalityDetailDialog
        open={hospDetailOpen}
        onOpenChange={setHospDetailOpen}
        hospitality={selectedHospitality}
        associations={allAssociations}
        usersMap={usersMap}
        canEditAccount={canEditAccount}
        onSuccess={invalidateHospitality}
        onHospitalityUpdate={setSelectedHospitality}
      />

      {/* File Preview Dialog */}
      <Dialog open={filePreviewOpen} onOpenChange={v => { setFilePreviewOpen(v); if (!v) { setIsEditingFile(false); setPreviewFileUrl(null); setPreviewFileType(null); setPreviewFileId(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Receipt Preview</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-black/5 rounded min-h-[200px]">
            {previewFileUrl && previewFileType?.startsWith('image/') ? (
              <img src={previewFileUrl} alt="Receipt" className="max-h-[60vh] object-contain" />
            ) : previewFileUrl && previewFileType === 'application/pdf' ? (
              <object data={previewFileUrl} type="application/pdf" className="w-full h-[60vh]">
                <p className="text-sm text-muted-foreground p-4">PDF preview unavailable. Use Download.</p>
              </object>
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <FileText className="w-16 h-16" />
                <p className="text-sm">Preview not available for this file type</p>
              </div>
            )}
          </div>
          {canEditAccount && (
            <div className="space-y-2">
              {isEditingFile ? (
                <div className="flex gap-2">
                  <Input value={editFileDescription} onChange={e => setEditFileDescription(e.target.value)} className="flex-1" placeholder="Description" />
                  <Button size="sm" onClick={handleUpdateFileDescription} disabled={updatingFile}>{updatingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
                  <Button size="sm" variant="outline" onClick={() => setIsEditingFile(false)}>Cancel</Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setIsEditingFile(true)}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={handleDeletePreviewFile} disabled={updatingFile}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              if (previewFileUrl) {
                const a = document.createElement('a'); a.href = previewFileUrl; a.download = 'receipt';
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
              }
            }} disabled={!previewFileUrl}>
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccountHospitalitySection;
